import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * One `?tab=<id>` deep-linkable tab selection — the sibling of
 * `useSheetParam`, and the same reasons: a notification/e-mail link must be
 * able to open a specific tab, and switching tabs must not pile up history
 * entries (`replace: true`).
 *
 * The fallback tab is the URL's ABSENCE — selecting it deletes the param, so
 * the default view keeps a clean address. An unknown or missing value reads
 * as the fallback, so a hand-edited URL can never land on a tab that does not
 * exist. `setTab` uses the FUNCTIONAL `setSearchParams` form, so it never
 * closes over a stale snapshot (a `?deal=`/`?upload=` param set elsewhere
 * survives a tab switch) and stays referentially stable.
 */
export function useTabParam<T extends string>(
  allowed: readonly T[],
  fallback: T,
): [T, (tab: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get('tab');
  const tab = allowed.includes(raw as T) ? (raw as T) : fallback;

  const setTab = useCallback(
    (next: T) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === fallback) params.delete('tab');
          else params.set('tab', next);
          return params;
        },
        { replace: true },
      );
    },
    [fallback, setSearchParams],
  );

  return [tab, setTab];
}
